import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import AuthenticationService from '@/services/admin/AuthenticationService'
import { useConfigStore } from '@/stores/admin/configStore'
import type { UsuarioAutenticacao } from '@/models/UsuarioAutenticacao'
import { getModulosVisiveis, ALL_MODULES } from '@/models/enums/ModuloConfig'
import api from '@/plugins/axios'

/**
 * Store Pinia de autenticacao.
 * Gerencia estado da sessao do usuario (token JWT, dados do perfil, roles, loja, segmento/plano).
 * Persiste dados em sessionStorage e sincroniza com o header Authorization do axios.
 *
 * Acoes principais:
 * - login: autentica e armazena dados da sessao
 * - logout: limpa sessao e cookie JWT
 * - checkAuth: revalida sessao com o backend via cookie
 */
export const useAuthStore = defineStore('auth', () => {
  const safeJsonParse = <T>(key: string, fallback: T): T => {
    try {
      const val = sessionStorage.getItem(key)
      return val ? JSON.parse(val) : fallback
    } catch { return fallback }
  }

  const token = ref<string | null>(sessionStorage.getItem('token'))
  const user = ref<string | null>(sessionStorage.getItem('user'))
  const nomeLoja = ref<string | null>(sessionStorage.getItem('nomeLoja'))
  const roles = ref<string[]>(safeJsonParse<string[]>('roles', []))
  const email = ref<string | null>(sessionStorage.getItem('email'))
  const usuarioid = ref<number | null>(
    sessionStorage.getItem('usuarioid') ? Number(sessionStorage.getItem('usuarioid')) : null
  )
  const perfilIds = ref<number[]>(safeJsonParse<number[]>('perfilIds', []))
  const segmento = ref<string | null>(sessionStorage.getItem('segmento'))
  const plano = ref<string | null>(sessionStorage.getItem('plano'))
  const lojaId = ref<number | null>(
    sessionStorage.getItem('lojaId') ? Number(sessionStorage.getItem('lojaId')) : null
  )
  // Versao do formato de modulosVisiveis persistido. Sessoes gravadas por versoes
  // anteriores (que sobrescreviam a lista com a taxonomia divergente de /config/ui)
  // sao descartadas na hidratacao, em vez de sobreviverem a um F5 — o guard de rota
  // nao revalida a sessao quando o token ja esta em sessionStorage.
  const MODULOS_SCHEMA_VERSION = '2'
  const hydrateModulos = (): string[] => {
    if (sessionStorage.getItem('modulosVisiveisVersao') !== MODULOS_SCHEMA_VERSION) {
      sessionStorage.removeItem('modulosVisiveis')
      return []
    }
    return safeJsonParse<string[]>('modulosVisiveis', [])
  }
  const modulosVisiveis = ref<string[]>(hydrateModulos())
  const permissoesFuncionais = ref<string[]>(safeJsonParse<string[]>('permissoesFuncionais', []))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  if (token.value) {
    AuthenticationService.setAuthHeader(token.value)
  }

  const isAuthenticated = computed(() => !!token.value)
  const getUser = computed(() => user.value)
  const getRoles = computed(() => roles.value)
  const isAdmin = computed(() => roles.value.some(r => r.toLowerCase() === 'administrador' || r.toLowerCase() === 'admin'))
  const getEmail = computed(() => email.value)
  const getNomeLoja = computed(() => nomeLoja.value)
  const getUsuarioId = computed(() => usuarioid.value)
  const getPerfilIds = computed(() => perfilIds.value)
  const getSegmento = computed(() => segmento.value)
  const getPlano = computed(() => plano.value)
  const getLojaId = computed(() => lojaId.value)
  const getModulosVis = computed(() => modulosVisiveis.value)

  const setPerfilIds = (v: number[]) => { perfilIds.value = v; sessionStorage.setItem('perfilIds', JSON.stringify(v)) }

  const setUser = (v: string) => { user.value = v; sessionStorage.setItem('user', v) }
  const setEmail = (v: string) => { email.value = v; sessionStorage.setItem('email', v) }
  const setRoles = (v: string[]) => { roles.value = v; sessionStorage.setItem('roles', JSON.stringify(v)) }
  const setNomeLoja = (v: string) => { nomeLoja.value = v; sessionStorage.setItem('nomeLoja', v) }
  const setUsuarioId = (v: number) => { usuarioid.value = v; sessionStorage.setItem('usuarioid', String(v)) }
  const setSegmento = (v: string) => { segmento.value = v; sessionStorage.setItem('segmento', v) }
  const setPlano = (v: string) => { plano.value = v; sessionStorage.setItem('plano', v) }
  const setLojaId = (v: number) => { lojaId.value = v; sessionStorage.setItem('lojaId', String(v)) }
  const setPermissoesFuncionais = (v: string[]) => {
    permissoesFuncionais.value = v
    sessionStorage.setItem('permissoesFuncionais', JSON.stringify(v))
  }

  const temPermissao = (codigo: string): boolean => {
    return permissoesFuncionais.value.includes(codigo)
  }

  const persistModulos = (modulos: string[]) => {
    modulosVisiveis.value = modulos
    sessionStorage.setItem('modulosVisiveis', JSON.stringify(modulos))
    sessionStorage.setItem('modulosVisiveisVersao', MODULOS_SCHEMA_VERSION)
  }

  const setModulosVisiveis = (seg: string, pln: string, segmentoModulosDb?: string[], planoModulosDb?: string[]) => {
    if (isAdmin.value) {
      persistModulos([...ALL_MODULES])
      return
    }
    persistModulos(getModulosVisiveis(seg, pln, segmentoModulosDb, planoModulosDb))
  }

  const setModulosVisiveisFromApi = (modulos: string[]) => {
    persistModulos(modulos)
  }

  const setToken = (v: string | null) => {
    token.value = v
    if (v) {
      sessionStorage.setItem('token', v)
      AuthenticationService.setAuthHeader(v)
    } else {
      sessionStorage.removeItem('token')
      AuthenticationService.setAuthHeader(null)
    }
  }

  const login = async (credentials: UsuarioAutenticacao) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await AuthenticationService.login(credentials)
      setUser(response.nome)
      setEmail(response.email)
      setRoles(response.roles)
      setUsuarioId(response.usuarioid)
      if (response.perfilIds) setPerfilIds(response.perfilIds)
      setPermissoesFuncionais(response.permissoesFuncionais || [])
      if (response.segmento) setSegmento(response.segmento)
      if (response.plano) setPlano(response.plano)
      if (response.lojaId) setLojaId(response.lojaId)
      // Token precisa estar gravado ANTES das chamadas abaixo: o interceptor do axios
      // le o token de sessionStorage. Sem isso as requisicoes saem sem Authorization e
      // dependem apenas do cookie jwt (SameSite=Strict), gerando 401 intermitente que
      // dispara logout automatico no interceptor de resposta.
      setToken(response.token)
      // After login success, fetch modulos from segmento/plano dominio
      const parseModulosStr = (val: any): string[] => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string' && val.trim()) return val.split(',').map((s: string) => s.trim()).filter(Boolean)
        return []
      }
      let segmentoModulosDb: string[] = []
      let planoModulosDb: string[] = []

      try {
        if (response.segmento) {
          const { data: segData } = await api.get(`/segmento-dominio/por-nome/${response.segmento}`)
          segmentoModulosDb = parseModulosStr(segData.modulos)
        }
      } catch { /* fallback to hardcoded */ }

      try {
        if (response.plano) {
          const { data: planoData } = await api.get(`/plano-dominio/por-nome/${response.plano}`)
          planoModulosDb = parseModulosStr(planoData.modulos)
        }
      } catch { /* fallback to hardcoded */ }

      if (response.segmento && response.plano) setModulosVisiveis(response.segmento, response.plano, segmentoModulosDb, planoModulosDb)
      // Admin sem loja: nome = "Admin"
      const adminUser = response.roles?.some((r: string) => r.toLowerCase() === 'administrador' || r.toLowerCase() === 'admin')
      setNomeLoja(response.nomeLoja || (adminUser ? 'Admin' : ''))

      // Carrega config de UI (mascaras, flags usaXxx por segmento).
      // NAO sobrescreve modulosVisiveis: o backend mantinha uma taxonomia de modulos
      // paralela e divergente (/clientes vs /cliente, /caixa apenas em COMPLETO_PDV,
      // /usuarios e /configuracoes liberados para todos). A fonte de verdade dos
      // modulos e ModuloConfig.getModulosVisiveis + tb_segmento_dominio/tb_plano_dominio.
      try {
        const configStore = useConfigStore()
        await configStore.fetchUiConfig()
      } catch { /* non-blocking */ }

      return response
    } catch (err: any) {
      error.value = err.response?.data?.mensagem || err.response?.data?.message || err.message || 'Erro ao fazer login'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const checkAuth = async () => {
    isLoading.value = true
    try {
      const response = await AuthenticationService.usuarioLogado()
      setUser(response.nome)
      setEmail(response.email)
      setRoles(response.roles)
      setNomeLoja(response.nomeLoja)
      setUsuarioId(response.usuarioid)
      if (response.perfilIds) setPerfilIds(response.perfilIds)
      setPermissoesFuncionais(response.permissoesFuncionais || [])
      if (response.segmento) setSegmento(response.segmento)
      if (response.plano) setPlano(response.plano)
      if (response.lojaId) setLojaId(response.lojaId)
      // Fetch modulos from segmento/plano dominio
      const parseModStr = (val: any): string[] => {
        if (Array.isArray(val)) return val
        if (typeof val === 'string' && val.trim()) return val.split(',').map((s: string) => s.trim()).filter(Boolean)
        return []
      }
      let segmentoModulosDb: string[] = []
      let planoModulosDb: string[] = []

      try {
        if (response.segmento) {
          const { data: segData } = await api.get(`/segmento-dominio/por-nome/${response.segmento}`)
          segmentoModulosDb = parseModStr(segData.modulos)
        }
      } catch { /* fallback to hardcoded */ }

      try {
        if (response.plano) {
          const { data: planoData } = await api.get(`/plano-dominio/por-nome/${response.plano}`)
          planoModulosDb = parseModStr(planoData.modulos)
        }
      } catch { /* fallback to hardcoded */ }

      if (response.segmento && response.plano) setModulosVisiveis(response.segmento, response.plano, segmentoModulosDb, planoModulosDb)
      if (response.token) setToken(response.token)
      // Carrega config de UI (mascaras/flags). Ver comentario em login():
      // modulosVisiveis NAO e sobrescrito pelo backend.
      try {
        const configStore = useConfigStore()
        await configStore.fetchUiConfig()
      } catch { /* non-blocking */ }
    } catch {
      error.value = 'Sessao expirada'
      logout()
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try { await AuthenticationService.logout() } catch {}
    setToken(null)
    user.value = null
    email.value = null
    roles.value = []
    nomeLoja.value = null
    usuarioid.value = null
    segmento.value = null
    plano.value = null
    lojaId.value = null
    modulosVisiveis.value = []
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('email')
    sessionStorage.removeItem('roles')
    sessionStorage.removeItem('nomeLoja')
    sessionStorage.removeItem('usuarioid')
    sessionStorage.removeItem('perfilIds')
    sessionStorage.removeItem('segmento')
    sessionStorage.removeItem('plano')
    sessionStorage.removeItem('lojaId')
    sessionStorage.removeItem('modulosVisiveis')
    sessionStorage.removeItem('modulosVisiveisVersao')
    sessionStorage.removeItem('permissoesFuncionais')
    perfilIds.value = []
    permissoesFuncionais.value = []
  }

  const clearError = () => { error.value = null }

  return {
    token, user, roles, nomeLoja, usuarioid, email, perfilIds,
    segmento, plano, lojaId, modulosVisiveis, permissoesFuncionais,
    isLoading, error,
    isAuthenticated, isAdmin, getUser, getRoles, getEmail, getNomeLoja, getUsuarioId, getPerfilIds,
    getSegmento, getPlano, getLojaId, getModulosVis,
    setUser, setUsuarioId, setEmail, setRoles, setNomeLoja, setToken, setPerfilIds,
    setSegmento, setPlano, setLojaId, setModulosVisiveis, setModulosVisiveisFromApi,
    setPermissoesFuncionais, temPermissao,
    login, logout, clearError, checkAuth,
  }
})
